export type Channel = "bacs" | "stripe" | "adflex" | "unknown";
export interface Signoff { [k: string]: unknown }
export interface Period {
  id: number; kind: "weekly" | "monthly"; start: string; end: string; account: string;
  status: "draft" | "signed"; opening: number | null; closing: number | null;
  ledgerOpening: number | null; ledgerClosing: number | null; syncedAt: string | null;
  coverageConfirmed: boolean; signoff: Signoff | null; createdBy: number | string | null; createdAt: string;
  ledgerProof?: {fileHash?: string; basis?: string; outsideDateRange?: number};
}
export interface Entry {
  id: number; side: "bank" | "merlin" | "provider"; day: string; description: string; reference: string | null;
  amount: number; channel: Channel; rule: string | null; evidence: unknown; decisionId: number | null; status: string; ageDays: number;
}
export interface Decision { id: number; kind: string; entryIds: number[]; details: Record<string, unknown>; actorId: number | string | null; createdAt: string }
export interface Suggestion { entryIds: number[]; channel: Channel; basis: string; bankAmount: number; ledgerAmount: number }
export interface Summary {
  bankIn: number; bankOut: number; opening: number | null; closing: number | null;
  ledgerOpening: number | null; ledgerClosing: number | null; difference: number | null;
  unresolved: number; agedExceptions: number; counts: Record<string, number>; ready: boolean; blockers: string[];
}
export interface ImportRec { id: number; filename: string; createdBy: number | string | null; createdAt: string; rowCount: number; summary?: unknown }
export interface Access { manage: boolean; signoff: boolean; admin?: boolean }
export interface Detail { period: Period; imports: ImportRec[]; entries: Entry[]; decisions: Decision[]; suggestions: Suggestion[]; summary: Summary; access: Access }
export interface PeriodList { periods: Period[]; access: Access; account: string; nominal: number }

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
export const apiUrl = (p: string) => `${BASE}/api/bank-reconciliation${p}`;

export async function brFetch<T>(path: string, init?: { method?: string; json?: unknown; form?: FormData }): Promise<T> {
  const method = init?.method ?? "GET";
  const headers: Record<string, string> = {};
  if (method !== "GET") headers["X-Requested-With"] = "XMLHttpRequest";
  let body: BodyInit | undefined;
  if (init?.json !== undefined) { headers["Content-Type"] = "application/json"; body = JSON.stringify(init.json); }
  if (init?.form) body = init.form;
  const res = await fetch(apiUrl(path), { method, headers, body, credentials: "include" });
  const text = await res.text();
  let data: any = null;
  try { data = text ? JSON.parse(text) : null; } catch { /* non-json */ }
  if (!res.ok) throw new Error(data?.error || data?.message || `Request failed (${res.status})`);
  return data as T;
}

export function formatPence(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "n/a";
  const neg = v < 0; const abs = Math.abs(Math.round(v));
  const s = `${Math.floor(abs / 100).toLocaleString("en-GB")}.${String(abs % 100).padStart(2, "0")}`;
  return `${neg ? "-" : ""}\u00a3${s}`;
}
export function poundsToPence(s: string): number | null {
  const t = s.replace(/[\u00a3,\s]/g, "");
  if (!/^-?\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(parseFloat(t) * 100);
}
export const STATUS_LABEL: Record<string, string> = {
  reconciled_bacs: "Reconciled (BACS)", reconciled_stripe: "Reconciled (Stripe)", reconciled_adflex: "Reconciled (Adflex)",
  review_required: "Review required", unidentified: "Unidentified", unmatched_bank: "Unmatched bank",
  unmatched_merlin: "Unmatched Merlin", timing_difference: "Timing difference", failed_posting: "Failed posting",
  settlement_difference: "Settlement difference", duplicate_other: "Duplicate / other",
};
export function statusTone(s: string): "ok" | "warn" | "bad" | "info" {
  if (s.startsWith("reconciled")) return "ok";
  if (s === "timing_difference") return "info";
  if (["review_required", "unidentified", "settlement_difference"].includes(s)) return "warn";
  return "bad";
}
/** Sum of selected entries by side, in pence. */
export function selectionTotals(entries: Entry[], ids: Set<number>) {
  let bank = 0, merlin = 0, provider = 0;
  for (const e of entries) if (ids.has(e.id)) { if (e.side === "bank") bank += e.amount; else if (e.side === "merlin") merlin += e.amount; else provider += e.amount; }
  return { bank, merlin, provider, diff: bank - merlin };
}
